import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { tokens, surface, hairline } from '../../../styles/designTokens';
import { FiX, FiDownload, FiClock, FiDatabase } from 'react-icons/fi';
import { GoCheck, GoSync } from 'react-icons/go';
import api from '../../../services/api';
import { toast } from 'react-toastify';

export default function ProcessamentoModal({ 
  isOpen, 
  onClose, 
  onComplete,
  outputId: initialOutputId 
}) {
  const [processing, setProcessing] = useState(false);
  const [taskProgress, setTaskProgress] = useState(0);
  const [taskStage, setTaskStage] = useState('');
  const [taskStats, setTaskStats] = useState([]);
  const [taskLogs, setTaskLogs] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fileDownloadProgress, setFileDownloadProgress] = useState(0);
  const [currentOutputId, setCurrentOutputId] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (initialOutputId) {
      setCurrentOutputId(initialOutputId);
    }
  }, [initialOutputId]);

  // Conectar WebSocket para receber atualizações em tempo real
  const connectWebSocket = (projectId, outputId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    
    if (!token) {
      toast.error('Token de autenticação não encontrado');
      return;
    }
    
    const ws = new WebSocket(`wss://log.vidal-app.com/ws?token=${token}`);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('✅ WebSocket conectado para download_sped');
      
      // Inscrever-se para receber mensagens
      ws.send(JSON.stringify({
        action: 'subscribe',
        projectId,
        type: 'download_sped'
      }));
    };
    
    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        console.log('[ProcessamentoModal] WebSocket raw message:', parsed);
        
        if (!parsed.success || !parsed.message) {
          console.log('[ProcessamentoModal] Message ignored - no success or message');
          return;
        }
        
        const msg = parsed.message;
        console.log('[ProcessamentoModal] Message type:', msg.type, 'Expected: download_sped');
        console.log('[ProcessamentoModal] Message projectId:', msg.projectId, 'Expected:', projectId);
        console.log('[ProcessamentoModal] Message outputId:', msg.data?.outputId, 'Expected:', outputId);
        
        // Verificar se é mensagem do tipo correto e outputId correto
        if (msg.type !== 'download_sped' || msg.projectId !== projectId) {
          console.log('[ProcessamentoModal] Message ignored - wrong type or projectId');
          return;
        }
        if (msg.data.outputId !== outputId) {
          console.log('[ProcessamentoModal] Message ignored - wrong outputId');
          return;
        }
        
        const { 
          progress, 
          stage, 
          message, 
          status, 
          totalSpeds, 
          totalEmpresas, 
          tamanhoZipMB,
          erros,
          empresaAtual,
          totalArquivos,
          arquivoAtual
        } = msg.data;
        
        // Atualizar progresso
        if (progress !== undefined && !isNaN(progress)) {
          setTaskProgress(progress);
        }
        
        if (stage) {
          setTaskStage(stage);
        }
        
        // Atualizar KPIs
        const stats = [];
        if (totalSpeds !== undefined) {
          stats.push({ label: 'SPEDs Encontrados', value: totalSpeds, type: 'success' });
        }
        if (totalEmpresas !== undefined) {
          stats.push({ label: 'Empresas', value: totalEmpresas, type: 'info' });
        }
        if (tamanhoZipMB !== undefined) {
          stats.push({ label: 'Tamanho ZIP', value: `${tamanhoZipMB} MB`, type: 'info' });
        }
        if (erros !== undefined && erros > 0) {
          stats.push({ label: 'Erros', value: erros, type: 'error' });
        }
        
        if (stats.length > 0) {
          setTaskStats(stats);
        }
        
        // Adicionar log
        if (message) {
          setTaskLogs(prev => [...prev, {
            message,
            timestamp: new Date().toLocaleTimeString('pt-BR'),
            type: status === 'error' ? 'error' : status === 'completed' ? 'success' : 'info',
            progress
          }]);
        }
        
        console.log('[ProcessamentoModal] WebSocket message:', {
          progress,
          stage,
          status,
          message
        });
        
        // Processamento concluído
        if (status === 'completed' || progress >= 100) {
          console.log('[ProcessamentoModal] ✅ Processamento concluído!');
          setProcessing(false);
          setTaskProgress(100);
          toast.success('Download de SPEDs concluído!', {
            autoClose: 5000
          });
          
          // Atualizar lista de downloads após conclusão
          if (onComplete) {
            setTimeout(() => {
              onComplete();
            }, 1000);
          }
        }
        
        if (status === 'error') {
          console.log('[ProcessamentoModal] ❌ Erro detectado:', message);
          setProcessing(false);
          toast.error(message || 'Erro no processamento');
        }
      } catch (error) {
        console.error('Erro ao processar mensagem WebSocket:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('Erro no WebSocket:', error);
      toast.error('Erro na conexão WebSocket');
    };
    
    ws.onclose = () => {
      console.log('WebSocket desconectado');
    };
  };

  // Iniciar processamento quando modal abrir
  useEffect(() => {
    if (isOpen && currentOutputId) {
      const projectId = localStorage.getItem('vidal_selected_project');
      console.log('[ProcessamentoModal] Iniciando - projectId:', projectId, 'outputId:', currentOutputId);
      
      if (projectId) {
        setProcessing(true);
        setTaskProgress(0);
        setTaskStage('Iniciando...');
        setTaskStats([]);
        setTaskLogs([]);
        
        // Conectar WebSocket
        console.log('[ProcessamentoModal] Conectando WebSocket...');
        connectWebSocket(projectId, currentOutputId);
      }
    }
  }, [isOpen, currentOutputId]);

  // Cleanup ao fechar modal
  useEffect(() => {
    if (!isOpen) {
      setProcessing(false);
      setTaskProgress(0);
      setTaskStage('');
      setTaskStats([]);
      setTaskLogs([]);
      setCurrentOutputId(null);
      
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!processing) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      onClose();
    }
  };

  const handleDownloadZip = async () => {
    try {
      setIsDownloading(true);
      setFileDownloadProgress(0);
      
      const response = await api.get(`/sped-download/download-zip`, {
        params: { outputId: currentOutputId },
        responseType: 'blob',
        timeout: 600000, // 10 min para ZIPs grandes
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
          setFileDownloadProgress(percentCompleted);
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `speds_${currentOutputId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download concluído!');
      
      setTimeout(() => {
        setIsDownloading(false);
        setFileDownloadProgress(0);
      }, 1000);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
      toast.error('Erro ao baixar arquivo ZIP');
      setIsDownloading(false);
      setFileDownloadProgress(0);
    }
  };

  // Build task items from stats + progress for the Tasks panel
  const buildTaskItems = () => {
    const items = [];
    const currentProgress = isDownloading ? fileDownloadProgress : taskProgress;
    const currentStageText = isDownloading ? 'Baixando arquivo...' : taskStage;

    // Stats as completed info items
    taskStats.forEach((stat, idx) => {
      items.push({
        id: `stat-${idx}`,
        name: `${stat.label}: ${typeof stat.value === 'string' ? stat.value : stat.value.toLocaleString('pt-BR')}`,
        status: 'completed',
        progress: 100
      });
    });

    // Current stage as running task
    if (processing || isDownloading) {
      items.push({
        id: 'current',
        name: currentStageText || 'Processando...',
        status: 'running',
        progress: currentProgress
      });
    }

    // If completed and not downloading, show completion
    if (currentProgress >= 100 && !processing && !isDownloading) {
      items.push({
        id: 'done',
        name: 'Download concluído!',
        status: 'completed',
        progress: 100
      });
    }

    return items;
  };

  const taskItems = buildTaskItems();
  const completedCount = taskItems.filter(t => t.status === 'completed').length;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="title-row">
              <FiDownload />
              <h2>Processando Download de SPEDs</h2>
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleClose} disabled={processing} title="Fechar">
              <FiX />
            </button>
          </div>
        </ModalHeader>

        {/* Header Período + Progresso Geral */}
        <SPDPeriodHeader>
          <SPDSectionTitle>Download de SPEDs</SPDSectionTitle>
          {taskStats.length > 0 && (
            <SPDPeriodBadge>
              <FiDatabase />
              {taskStats.map(s => `${s.label}: ${typeof s.value === 'string' ? s.value : s.value.toLocaleString('pt-BR')}`).join(' · ')}
            </SPDPeriodBadge>
          )}
          <SPDProgressSection>
            <SPDProgressInfo>
              <SPDProgressLabel>Progresso Geral</SPDProgressLabel>
              <SPDProgressCount>{Math.round(isDownloading ? fileDownloadProgress : taskProgress)}%</SPDProgressCount>
            </SPDProgressInfo>
            <SPDProgressBarContainer>
              <SPDProgressBarFill $progress={isDownloading ? fileDownloadProgress : taskProgress} />
            </SPDProgressBarContainer>
          </SPDProgressSection>
        </SPDPeriodHeader>

        <ModalBody>
          <ProcessingSection>
            <ProcessingGrid>
              {/* Painel de Tarefas */}
              <TasksPanel>
                <PanelHeader>
                  <PanelLabel>TAREFAS</PanelLabel>
                  <PanelBadge $done={taskProgress >= 100 && !processing}>
                    {processing || isDownloading
                      ? `${completedCount}/${taskItems.length}`
                      : 'Concluído'
                    }
                  </PanelBadge>
                </PanelHeader>
                <TasksList>
                  {taskItems.map(task => (
                    <TaskItem key={task.id} $status={task.status}>
                      <TaskStatusDot $status={task.status}>
                        {task.status === 'completed' && <GoCheck />}
                        {task.status === 'running' && <GoSync className="spinning" />}
                      </TaskStatusDot>
                      <TaskContent>
                        <TaskName $status={task.status}>{task.name}</TaskName>
                        {task.status === 'running' && task.progress > 0 && (
                          <TaskProgressBar>
                            <TaskProgressFill $progress={task.progress} />
                          </TaskProgressBar>
                        )}
                      </TaskContent>
                      {task.status === 'running' && task.progress > 0 && (
                        <TaskPercent>{Math.round(task.progress)}%</TaskPercent>
                      )}
                    </TaskItem>
                  ))}
                </TasksList>
              </TasksPanel>

              {/* Painel de Logs */}
              <LogsPanel>
                <PanelHeader>
                  <PanelLabel>TERMINAL</PanelLabel>
                  <LogCounter>{taskLogs.length} linhas</LogCounter>
                </PanelHeader>
                <LogsEditor>
                  {taskLogs.length === 0 ? (
                    <EmptyLogs>
                      <span>$</span> aguardando saída...
                    </EmptyLogs>
                  ) : (
                    [...taskLogs].reverse().map((log, index) => (
                      <LogLine key={index} $type={log.type}>
                        <LogLineNumber>{taskLogs.length - index}</LogLineNumber>
                        <LogTimestamp>{log.timestamp}</LogTimestamp>
                        <LogText $type={log.type}>{log.message}</LogText>
                      </LogLine>
                    ))
                  )}
                </LogsEditor>
              </LogsPanel>
            </ProcessingGrid>
          </ProcessingSection>
        </ModalBody>

        {taskProgress >= 100 && !processing && (
          <ModalFooter>
            <FooterButton className="cancel" onClick={handleClose}>
              Fechar
            </FooterButton>
            <FooterButton className="primary" onClick={handleDownloadZip} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <FiClock className="spinner" />
                  Baixando...
                </>
              ) : (
                <>
                  <FiDownload />
                  Baixar Arquivo ZIP
                </>
              )}
            </FooterButton>
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
}

// ============= STYLED COMPONENTS =============

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const spinKf = keyframes`
  to { transform: rotate(360deg); }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: 0.6em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(12px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${fadeIn} ${tokens.durBase} ${tokens.easeOut};

  .spin { animation: ${spinKf} 1s linear infinite; }
`;

const ModalContainer = styled.div`
  position: relative;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusLg};
  width: 100%;
  max-width: 640px;
  height: min(720px, 90vh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 1px 0 0 rgba(255, 255, 255, 0.04) inset,
    0 24px 64px rgba(0, 0, 0, 0.45),
    0 8px 16px rgba(0, 0, 0, 0.18);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${tokens.brandRing}, transparent);
    pointer-events: none;
    z-index: 2;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  flex-shrink: 0;

  .header-left {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;

    .title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      color: ${({ theme }) => theme.text};

      svg { font-size: 0.95em; color: ${tokens.brand}; flex-shrink: 0; }

      h2, h3 {
        margin: 0;
        font-size: 0.92em;
        font-weight: 600;
        letter-spacing: -0.015em;
        color: ${({ theme }) => theme.text};
      }
    }

    .header-subtitle, .subtitle {
      ${mono};
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      margin-left: 20px;
    }
  }

  .close-btn, .modal-close, button.close {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${tokens.radiusSm};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    opacity: 0.5;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 0.85em; }

    &:hover:not(:disabled) {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme)};
    }

    &:disabled { opacity: 0.25; cursor: not-allowed; }
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const ModalFooter = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 12px 18px 14px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 0)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
    opacity: 0.4;
    pointer-events: none;
  }
`;

const FooterButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: ${tokens.radiusSm};
  font-family: ${tokens.fontSans};
  font-size: 0.78em;
  font-weight: 500;
  cursor: pointer;
  transition: all ${tokens.durBase} ${tokens.easeOut};
  letter-spacing: -0.005em;
  white-space: nowrap;
  background: transparent;
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => hairline(theme)};
  opacity: 0.85;

  svg { font-size: 0.95em; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => surface(theme, 1)};
    border-color: ${({ theme }) => hairline(theme, true)};
    opacity: 1;
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }

  ${({ $primary }) =>
    $primary &&
    css`
      background: ${tokens.brand};
      border-color: ${tokens.brand};
      color: white;
      opacity: 1;
      box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.08) inset;

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    `}
`;

const SPDPeriodHeader = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

const SPDSectionTitle = styled.h4`
  ${eyebrow};
  margin: 0;
  font-size: 0.6em;
  opacity: 0.55;
`;

const SPDPeriodBadge = styled.div`
  ${mono};
  align-self: flex-start;
  padding: 4px 10px;
  background: ${tokens.brandSoft};
  color: ${tokens.brand};
  border: 1px solid ${tokens.brandRing};
  border-radius: ${tokens.radiusPill};
  font-size: 0.66em;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg { font-size: 0.95em; }
`;

const SPDProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0 4px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
`;

const SPDProgressInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SPDProgressLabel = styled.span`
  ${mono};
  font-size: 0.62em;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.text};
  opacity: 0.55;
`;

const SPDProgressCount = styled.span`
  ${mono};
  font-size: 0.74em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  letter-spacing: -0.01em;
`;

const SPDProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => surface(theme, 2)};
  border-radius: ${tokens.radiusPill};
  overflow: hidden;
`;

const SPDProgressBarFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${tokens.brand};
  border-radius: ${tokens.radiusPill};
  transition: width 0.3s ${tokens.easeOut};
`;

const ProcessingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ProcessingGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  background: ${({ theme }) => surface(theme, 1)};
`;

const PanelLabel = styled.span`
  ${eyebrow};
  font-size: 0.58em;
  opacity: 0.6;
`;

const PanelBadge = styled.span`
  ${mono};
  padding: 2px 8px;
  border-radius: ${tokens.radiusPill};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid;

  ${({ $done }) =>
    $done
      ? css`
          background: ${tokens.brandSoft};
          color: ${tokens.brand};
          border-color: ${tokens.brandRing};
        `
      : css`
          background: rgba(59, 130, 246, 0.10);
          color: #3b82f6;
          border-color: rgba(59, 130, 246, 0.25);
        `}
`;

const LogCounter = styled.span`
  ${mono};
  font-size: 0.6em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  letter-spacing: 0.02em;
`;

const TasksPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
`;

const TasksList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 8px 16px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
`;

const TaskStatusDot = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) => {
    if ($status === 'completed') return tokens.brand;
    if ($status === 'running') return 'rgba(59, 130, 246, 0.18)';
    if ($status === 'error') return tokens.dangerSoft;
    return 'transparent';
  }};
  border: 1px solid ${({ $status }) => {
    if ($status === 'completed') return tokens.brand;
    if ($status === 'running') return 'rgba(59, 130, 246, 0.45)';
    if ($status === 'error') return 'rgba(239, 68, 68, 0.45)';
    return 'rgba(255,255,255,0.18)';
  }};
  color: ${({ $status }) => $status === 'completed' ? '#fff' : 'transparent'};

  svg { font-size: 0.7em; }
`;

const TaskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TaskName = styled.div`
  ${mono};
  font-size: 0.7em;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.text};
  opacity: ${({ $status }) => ($status === 'pending' ? 0.45 : 0.85)};
  ${({ $status }) => $status === 'completed' && 'text-decoration: line-through; opacity: 0.55;'}
`;

const TaskProgressBar = styled.div`
  width: 100%;
  height: 2px;
  background: rgba(59, 130, 246, 0.10);
  border-radius: ${tokens.radiusPill};
  overflow: hidden;
`;

const TaskProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: rgba(59, 130, 246, 0.6);
  border-radius: ${tokens.radiusPill};
  transition: width 0.4s ${tokens.easeOut};
`;

const TaskPercent = styled.div`
  ${mono};
  font-size: 0.6em;
  color: rgba(59, 130, 246, 0.7);
  letter-spacing: 0.02em;
`;

const LogsPanel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

const LogsEditor = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => surface(theme, 2)};
  padding: 8px 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 3px;
  }
`;

const EmptyLogs = styled.div`
  padding: 24px 16px;
  ${mono};
  text-align: center;
  font-size: 0.7em;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  letter-spacing: 0.02em;
`;

const LogLine = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1px 0;
  ${mono};
  font-size: 0.66em;
  line-height: 1.5;
  letter-spacing: 0.01em;

  &:hover {
    background: ${({ theme }) => surface(theme, 1)};
  }
`;

const LogLineNumber = styled.span`
  width: 38px;
  text-align: right;
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.3;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => hairline(theme)};
`;

const LogTimestamp = styled.span`
  padding: 0 8px;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
  flex-shrink: 0;
`;

const LogText = styled.span`
  flex: 1;
  padding-right: 12px;
  word-break: break-word;
  color: ${({ theme }) => theme.text};
  opacity: 0.85;

  &.error   { color: ${tokens.danger}; }
  &.warn    { color: ${tokens.warn}; }
  &.success { color: ${tokens.brand}; }
  &.info    { opacity: 0.7; }
  &.debug   { opacity: 0.45; }
`;
